/**
 * Inline SVG charts.
 *
 * No charting library: this is two shapes, and Recharts plus its D3 dependencies
 * would be the largest thing in the bundle by an order of magnitude. Hand-drawn
 * SVG also means the colours come from the same CSS custom properties as the rest
 * of the UI, so light and dark stay consistent for free.
 */

import type { Analytics } from '../lib/api';
import { formatDate } from './ui';

type Series = { key: keyof Analytics['timeseries'][number]; label: string; color: string };

const SERIES: Series[] = [
  { key: 'delivered', label: 'Delivered', color: 'var(--ok)' },
  { key: 'bounced', label: 'Bounced', color: 'var(--danger)' },
  { key: 'opened', label: 'Opened', color: 'var(--info)' },
  { key: 'clicked', label: 'Clicked', color: 'var(--accent)' },
];

export function TimeseriesChart({ points }: { points: Analytics['timeseries'] }) {
  if (points.length === 0) {
    return <div className="empty">No activity in this window yet.</div>;
  }

  const width = 720;
  const height = 168;
  const padding = { top: 10, right: 8, bottom: 22, left: 34 };
  const plotWidth = width - padding.left - padding.right;
  const plotHeight = height - padding.top - padding.bottom;

  const maxValue = Math.max(
    1,
    ...points.flatMap((point) => SERIES.map((series) => Number(point[series.key]))),
  );

  // A single point has no line to draw, so it is placed mid-plot rather than at
  // x=0 where it would sit on the axis and look like an error.
  const xFor = (index: number) =>
    points.length === 1 ? plotWidth / 2 : (index / (points.length - 1)) * plotWidth;
  const yFor = (value: number) => plotHeight - (value / maxValue) * plotHeight;

  const gridLines = [0, 0.5, 1];

  return (
    <div>
      <svg
        className="chart"
        viewBox={`0 0 ${width} ${height}`}
        preserveAspectRatio="none"
        role="img"
        aria-label="Delivery, bounce, open and click counts over time"
      >
        <g transform={`translate(${padding.left},${padding.top})`}>
          {gridLines.map((fraction) => (
            <g key={fraction}>
              <line
                x1={0}
                x2={plotWidth}
                y1={plotHeight * fraction}
                y2={plotHeight * fraction}
                stroke="var(--border)"
                strokeWidth={1}
              />
              <text
                x={-8}
                y={plotHeight * fraction + 4}
                textAnchor="end"
                fontSize={10}
                fill="var(--text-faint)"
              >
                {Math.round(maxValue * (1 - fraction))}
              </text>
            </g>
          ))}

          {SERIES.map((series) => {
            const path = points
              .map((point, index) => {
                const command = index === 0 ? 'M' : 'L';
                return `${command}${xFor(index).toFixed(1)},${yFor(Number(point[series.key])).toFixed(1)}`;
              })
              .join(' ');
            return (
              <g key={String(series.key)}>
                <path
                  d={path}
                  fill="none"
                  stroke={series.color}
                  strokeWidth={1.8}
                  strokeLinejoin="round"
                  strokeLinecap="round"
                  vectorEffect="non-scaling-stroke"
                />
                {points.map((point, index) => (
                  <circle
                    key={point.date}
                    cx={xFor(index)}
                    cy={yFor(Number(point[series.key]))}
                    r={2.2}
                    fill={series.color}
                  />
                ))}
              </g>
            );
          })}

          {/* Only first, middle and last labels: a 30-day window would otherwise
              produce overlapping text at this width. */}
          {[0, Math.floor(points.length / 2), points.length - 1]
            .filter((index, position, all) => all.indexOf(index) === position)
            .map((index) => (
              <text
                key={index}
                x={xFor(index)}
                y={plotHeight + 15}
                textAnchor="middle"
                fontSize={10}
                fill="var(--text-faint)"
              >
                {formatDate(points[index]!.date)}
              </text>
            ))}
        </g>
      </svg>

      <div className="chart-legend">
        {SERIES.map((series) => (
          <span className="legend-key" key={String(series.key)}>
            <span className="legend-swatch" style={{ background: series.color }} />
            {series.label}
          </span>
        ))}
      </div>
    </div>
  );
}

export function BarList({
  rows,
  emptyLabel,
}: {
  rows: { label: string; sent: number; delivered: number; bounced: number }[];
  emptyLabel: string;
}) {
  if (rows.length === 0) return <div className="empty">{emptyLabel}</div>;

  const max = Math.max(1, ...rows.map((row) => row.sent));

  return (
    <div>
      {rows.map((row) => (
        <div className="bar-row" key={row.label}>
          <span className="truncate mono" title={row.label}>
            {row.label}
          </span>
          <span className="bar-track">
            <span
              className="bar-fill"
              style={{
                width: `${(row.sent / max) * 100}%`,
                // Bounces in this slice tint the bar, so a bad segment is
                // visible without reading the numbers.
                background: row.bounced > 0 ? 'var(--warn)' : 'var(--accent)',
              }}
            />
          </span>
          <span className="mono" style={{ textAlign: 'right', color: 'var(--text-muted)' }}>
            {row.sent}
            {row.bounced > 0 && (
              <span style={{ color: 'var(--danger)' }}> ·{row.bounced}</span>
            )}
          </span>
        </div>
      ))}
    </div>
  );
}
